/**
 * UAVTradeZone - Notifications Page
 * Date: 2026-02-09
 *
 * Displays a list of in-app notifications (email records) with dismiss all functionality.
 * Notifications serve as a backup for emails that may have gone to spam.
 */

import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  fetchNotifications,
  dismissAllNotifications,
} from "../services/notificationsService";
import { Notification } from "../interfaces/notification";
import NotificationCard from "../features/notifications/components/NotificationCard";
import "../features/notifications/styles/notifications.scss";

const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  const loadNotifications = useCallback(async (pageNum: number, append: boolean = false) => {
    try {
      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }
      setError(null);

      const response = await fetchNotifications(pageNum, 20);

      if (response.success) {
        const newNotifications = response.data.notifications;
        if (append) {
          setNotifications((prev) => [...prev, ...newNotifications]);
        } else {
          setNotifications(newNotifications);
        }
        setHasMore(response.data.hasMore);
        setPage(pageNum);
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to load notifications";
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications(0);
  }, [loadNotifications]);

  // [2026-02-09] Clear badge when page loads and no undismissed notifications exist
  useEffect(() => {
    if (!loading && notifications.length > 0) {
      const hasUndismissed = notifications.some((n) => !n.dismissedAt);
      if (!hasUndismissed) {
        // All notifications are dismissed - clear the navbar badge
        window.dispatchEvent(new CustomEvent("notificationsDismissed"));
      }
    }
  }, [loading, notifications]);

  const handleLoadMore = () => {
    if (!loadingMore && hasMore) {
      loadNotifications(page + 1, true);
    }
  };

  const handleDismissAll = async () => {
    try {
      setDismissing(true);
      await dismissAllNotifications();

      // Update local state to mark all as dismissed
      setNotifications((prev) =>
        prev.map((n) => ({
          ...n,
          dismissedAt: n.dismissedAt || new Date().toISOString(),
        }))
      );

      // Dispatch event to update navbar badge
      window.dispatchEvent(new CustomEvent("notificationsDismissed"));

      toast.success("All notifications dismissed");
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to dismiss notifications";
      toast.error(message);
    } finally {
      setDismissing(false);
    }
  };

  // Check if there are any new (undismissed) notifications
  const hasNewNotifications = notifications.some((n) => !n.dismissedAt);

  if (loading) {
    return (
      <div className="notifications-page">
        <div className="notifications-page__loading">
          <span className="u-P2-16">Loading notifications...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="notifications-page">
        <div className="notifications-page__error">
          <span className="u-P2-16 text-color-status-red">{error}</span>
          <button
            type="button"
            className="btn-figma-1 mt-3"
            onClick={() => loadNotifications(0)}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="notifications-page">
      <div className="notifications-page__header">
        <h1 className="notifications-page__title u-H2-24">Notifications</h1>
        {notifications.length > 0 && hasNewNotifications && (
          <button
            type="button"
            className="btn-figma-1"
            onClick={handleDismissAll}
            disabled={dismissing}
          >
            {dismissing ? "Dismissing..." : "Dismiss All"}
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="notifications-empty">
          <img
            src="/assets/notifications.svg"
            alt=""
            className="notifications-empty__icon"
            aria-hidden="true"
          />
          <h3 className="notifications-empty__title u-P1-18-semi-bold">
            No notifications yet
          </h3>
          <p className="notifications-empty__description u-P3-14-medium">
            You'll see notifications here when you receive broadcasts, upload inventory, or have other activity.
          </p>
        </div>
      ) : (
        <>
          <div className="notifications-page__list">
            {notifications.map((notification) => (
              <NotificationCard
                key={notification._id}
                notification={notification}
              />
            ))}
          </div>

          {hasMore && (
            <div className="notifications-page__load-more">
              <button
                type="button"
                className="btn-figma-1"
                onClick={handleLoadMore}
                disabled={loadingMore}
              >
                {loadingMore ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default NotificationsPage;
